/**
 * Utility functions for creating mesh gradients
 */

/**
 * Creates a vibrant mesh gradient from RGB color strings
 * @param primaryColor - Primary RGB color string (e.g., 'rgb(18,18,18)')
 * @param secondaryColor - Secondary RGB color string (e.g., 'rgb(38,38,38)')
 * @returns CSS style object with backgroundColor and backgroundImage properties
 */
export function createMeshGradient(primaryColor: string | undefined, secondaryColor: string | undefined) {
  // Ensure we have string values
  const primary = primaryColor || 'rgb(18,18,18)';
  const secondary = secondaryColor || 'rgb(38,38,38)';
  
  // Function to generate a random position
  const getRandomPosition = () => `${Math.random() * 100}% ${Math.random() * 100}%`;
  
  // Function to convert RGB to HSL
  const rgbToHsl = (rgbStr: string): [number, number, number] => {
    // Extract RGB values
    const rgbMatch = rgbStr.match(/rgb\((\d+),(\d+),(\d+)\)/);
    if (!rgbMatch) return [0, 0, 20]; // Default to dark gray in HSL
    
    // Use non-null assertion since we've already checked for null
    const r = parseInt(rgbMatch[1]!) / 255;
    const g = parseInt(rgbMatch[2]!) / 255;
    const b = parseInt(rgbMatch[3]!) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      
      h /= 6;
    }
    
    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  };
  
  // Get HSL values from the dark colors
  const primaryHsl = rgbToHsl(primary);
  const secondaryHsl = rgbToHsl(secondary);
  
  // Generate color variations based on the primary and secondary colors
  const generateColorVariations = (): string[] => {
    const colors: string[] = [];
    
    // Original colors with slight adjustments
    // 1. Primary color (original)
    colors.push(`hsla(${primaryHsl[0]}, ${primaryHsl[1]}%, ${primaryHsl[2]}%, 0.85)`);
    
    // 2. Secondary color (original)
    colors.push(`hsla(${secondaryHsl[0]}, ${secondaryHsl[1]}%, ${secondaryHsl[2]}%, 0.85)`);
    
    // Variations of primary color
    // 3. Slightly lighter primary
    colors.push(`hsla(${primaryHsl[0]}, ${primaryHsl[1]}%, ${Math.min(100, primaryHsl[2] + 15)}%, 0.75)`);
    
    // 4. Slightly more saturated primary
    colors.push(`hsla(${primaryHsl[0]}, ${Math.min(100, primaryHsl[1] + 20)}%, ${primaryHsl[2]}%, 0.75)`);
    
    // 5. Slight hue shift of primary (small step clockwise)
    const primaryHueShift1 = (primaryHsl[0] + 15) % 360;
    colors.push(`hsla(${primaryHueShift1}, ${primaryHsl[1]}%, ${primaryHsl[2]}%, 0.7)`);
    
    // Variations of secondary color
    // 6. Slightly lighter secondary
    colors.push(`hsla(${secondaryHsl[0]}, ${secondaryHsl[1]}%, ${Math.min(100, secondaryHsl[2] + 15)}%, 0.75)`);
    
    // 7. Slightly more saturated secondary
    colors.push(`hsla(${secondaryHsl[0]}, ${Math.min(100, secondaryHsl[1] + 20)}%, ${secondaryHsl[2]}%, 0.75)`);
    
    // 8. Slight hue shift of secondary (small step clockwise)
    const secondaryHueShift1 = (secondaryHsl[0] + 15) % 360;
    colors.push(`hsla(${secondaryHueShift1}, ${secondaryHsl[1]}%, ${secondaryHsl[2]}%, 0.7)`);
    
    return colors;
  };
  
  // Get our color values
  const colorValues = generateColorVariations();
  
  // Create a colorful mesh gradient with the colors
  const backgroundGradients = colorValues.map((color, index) => 
    `radial-gradient(at ${getRandomPosition()}, ${color} 0px, transparent ${45 + (index * 3)}%)`
  );
  
  return {
    backgroundColor: primary,
    backgroundImage: backgroundGradients.join(', ')
  };
}

/**
 * Creates a safe linear gradient string from two colors
 * @param primaryColor - Primary color string
 * @param secondaryColor - Secondary color string
 * @returns Linear gradient CSS string
 */
export function createLinearGradient(primaryColor: string | undefined, secondaryColor: string | undefined) {
  const primary = primaryColor || 'rgb(18,18,18)';
  const secondary = secondaryColor || 'rgb(38,38,38)';
  return `linear-gradient(to top right, ${primary}, ${secondary})`;
} 